import { useState } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { Seo } from "@/components/Seo";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const expired = new URLSearchParams(location.search).get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/admin");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] px-4">
      <Seo title="Admin Login | Quinte BCA Pool League" path="/admin/login" noindex />
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-8">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-6" data-testid="login-logo-link">
          <img src={LOGO_URL} alt="Quinte BCA Pool League" className="h-12 w-12 object-contain" />
          <span className="font-heading font-extrabold text-lg">Quinte BCA Pool League</span>
        </Link>
        <div className="flex items-center gap-2 justify-center text-[#555555] mb-6">
          <Lock className="h-4 w-4" />
          <h1 className="font-heading font-bold text-xl text-[#111111]">Admin Login</h1>
        </div>

        {expired && !error && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-4 py-3 mb-4" data-testid="login-expired">
            Your session expired. Please log in again to continue.
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#333333] mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full bg-[#1E63C4] text-white rounded-md py-3 font-semibold hover:bg-[#174e9c] transition-colors disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
